import { Alert, Descriptions, Empty, Image, Space, Statistic, Table, Tabs, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { Alarm, IMUAnalysis, IMUBucket, ReplayFrame, SessionManifest, TrajectoryCandidate } from '../types'
import { relativeSeconds } from '../lib/time'
import { IMUChart } from './IMUChart'

const severityColor: Record<string, string> = { info: 'blue', warning: 'gold', error: 'red', critical: 'magenta' }
const hex = (value: number) => `0x${(value >>> 0).toString(16).toUpperCase()}`

export function Diagnostics({
  manifest,
  frame,
  imuBuckets,
  imuAnalysis,
}: {
  manifest: SessionManifest
  frame: ReplayFrame
  imuBuckets: IMUBucket[]
  imuAnalysis?: IMUAnalysis
}) {
  const alarmColumns: ColumnsType<Alarm> = [
    { title: '级别', dataIndex: 'sourceLevel', width: 88, render: (value, item) => <Tag color={severityColor[item.severity] ?? 'default'}>{value}</Tag> },
    { title: '代码', dataIndex: 'code', width: 112, render: (value) => <Typography.Text code>{value}</Typography.Text> },
    { title: '状态', dataIndex: 'sourceStatus', width: 92, render: (value, item) => <Tag color={item.status === 'resolved' ? 'green' : item.status === 'active' ? 'red' : 'default'}>{value}</Tag> },
    { title: '消息', dataIndex: 'message' },
    { title: '时间', dataIndex: 'timestampNs', width: 82, render: (value) => `${relativeSeconds(manifest.session.startNs, value).toFixed(1)}s` },
    { title: '恢复', dataIndex: 'resolvedNs', width: 82, render: (value) => value ? `${relativeSeconds(manifest.session.startNs, value).toFixed(1)}s` : '-' },
  ]

  const selected = frame.trajectories.find((item) => item.selected)
  const trajectoryColumns: ColumnsType<TrajectoryCandidate> = [
    { title: '候选', dataIndex: 'id', width: 110, render: (value, item) => <Tag color={item.selected ? 'gold' : 'default'}>{value}</Tag> },
    { title: 'v', dataIndex: 'speedV', width: 72, render: (value) => Number(value).toFixed(3) },
    { title: 'w', dataIndex: 'speedW', width: 72, render: (value) => Number(value).toFixed(3) },
    { title: '总代价', dataIndex: ['costs', 'total'], width: 82, render: (value, item) => Number(value ?? item.cost).toFixed(3) },
    { title: '地图', dataIndex: ['costs', 'map'], width: 72, render: (value) => Number(value).toFixed(3) },
    { title: '速度', dataIndex: ['costs', 'speed'], width: 72, render: (value) => Number(value).toFixed(3) },
    { title: '朝向', dataIndex: ['costs', 'yaw'], width: 72, render: (value) => Number(value).toFixed(3) },
    { title: '距离', dataIndex: ['costs', 'distance'], width: 72, render: (value) => Number(value).toFixed(3) },
    { title: 'X 偏移', dataIndex: ['costs', 'xShift'], width: 74, render: (value) => Number(value).toFixed(3) },
    { title: 'Y 偏移', dataIndex: ['costs', 'yShift'], width: 74, render: (value) => Number(value).toFixed(3) },
  ]
  const cameraTab = (
    <div className="camera-grid">
      {frame.cameras.map((camera) => (
        <div className="camera-card" key={camera.camera}>
          <div className="camera-title">
            <span>{camera.camera === 'front' ? '前置摄像头' : camera.camera === 'front_reco' ? '前置识别画面' : '顶部摄像头'}</span>
            <Typography.Text type="secondary">{relativeSeconds(manifest.session.startNs, camera.timestampNs).toFixed(2)}s</Typography.Text>
          </div>
          <Image preview={false} src={camera.url} />
        </div>
      ))}
    </div>
  )

  const alarmTab = frame.alarms.length ? (
    <Table rowKey="id" size="small" pagination={{ pageSize: 8, size: 'small' }} columns={alarmColumns} dataSource={frame.alarms} scroll={{ x: 780 }} />
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="截至当前时刻无报警事件" />
  )

  const plannerTab = (
    <Space orientation="vertical" size={14} style={{ width: '100%' }}>
      <div className="stat-grid">
        <Statistic title="DWA 候选" value={frame.trajectories.length} suffix="条" />
        <Statistic title="选中代价" value={selected?.cost ?? 0} precision={3} />
        <Statistic title="定位策略" value={frame.strategyPoses.length} suffix="种" />
        <Statistic title="协同机器人" value={frame.peerRobots.length} suffix="台" />
        <Statistic
          title="丢定位风险"
          value={frame.localizationHealth ? frame.localizationHealth.lostProbability * 100 : '-'}
          precision={2}
          suffix={frame.localizationHealth ? '%' : undefined}
          valueStyle={{
            color: frame.localizationHealth?.status === 'lost' ? '#ff5d72' : frame.localizationHealth?.status === 'warning' ? '#ffc14f' : '#52d6ad',
          }}
        />
      </div>
      <Descriptions
        size="small"
        column={2}
        bordered
        items={[
          { key: 'targetV', label: '目标线速度', children: `${frame.pid.targetV.toFixed(3)} m/s` },
          { key: 'actualV', label: '实际线速度', children: `${frame.pid.actualV.toFixed(3)} m/s` },
          { key: 'targetW', label: '目标角速度', children: `${frame.pid.targetW.toFixed(3)} rad/s` },
          { key: 'actualW', label: '实际角速度', children: `${frame.pid.actualW.toFixed(3)} rad/s` },
          { key: 'commandV', label: '指令线速度', children: `${frame.pid.commandV.toFixed(3)} m/s` },
          { key: 'commandW', label: '指令角速度', children: `${frame.pid.commandW.toFixed(3)} rad/s` },
          { key: 'left', label: '左轮输出', children: frame.pid.outputLeft.toFixed(3) },
          { key: 'right', label: '右轮输出', children: frame.pid.outputRight.toFixed(3) },
          { key: 'mode', label: '控制模式', children: frame.pid.mode || '-' },
          { key: 'yawError', label: 'Yaw 误差', children: frame.pid.yawError.toFixed(3) },
          { key: 'distError', label: '距离误差', children: frame.pid.distError.toFixed(3) },
          { key: 'shiftError', label: '横向误差', children: frame.pid.shiftError.toFixed(3) },
          { key: 'wError', label: '角速度误差', children: frame.pid.wError.toFixed(3) },
          { key: 'legacyMotion', label: '旧版 Motion v / w', children: frame.motion ? `${frame.motion.linearRaw} / ${frame.motion.angularRaw}（原始整数）` : '-' },
        ]}
      />
      {(frame.boardTelemetry || frame.boardCommand) && (
        <Descriptions
          title="主控板 Tick / Tock"
          size="small"
          column={3}
          bordered
          items={[
            { key: 'tickTime', label: '状态帧', children: frame.boardTelemetry ? `${relativeSeconds(manifest.session.startNs, frame.boardTelemetry.timestampNs).toFixed(3)}s` : '-' },
            { key: 'tockTime', label: '指令帧', children: frame.boardCommand ? `${relativeSeconds(manifest.session.startNs, frame.boardCommand.timestampNs).toFixed(3)}s` : '-' },
            { key: 'motorState', label: '电机状态', children: frame.boardTelemetry ? hex(frame.boardTelemetry.motorState) : '-' },
            { key: 'actualLeft', label: '左轮实际', children: frame.boardTelemetry ? `${frame.boardTelemetry.leftMotorSpeed} mm/s` : '-' },
            { key: 'actualRight', label: '右轮实际', children: frame.boardTelemetry ? `${frame.boardTelemetry.rightMotorSpeed} mm/s` : '-' },
            { key: 'commandSpeed', label: '左右轮指令', children: frame.boardCommand ? `${frame.boardCommand.leftMotorSpeed} / ${frame.boardCommand.rightMotorSpeed} mm/s` : '-' },
            { key: 'encoder', label: '左右编码器', children: frame.boardTelemetry ? `${frame.boardTelemetry.leftMotorPosition} / ${frame.boardTelemetry.rightMotorPosition}` : '-' },
            { key: 'recvTicks', label: '左右电机接收时刻', children: frame.boardTelemetry ? `${frame.boardTelemetry.leftRecvTimeTick} / ${frame.boardTelemetry.rightRecvTimeTick} ms` : '-' },
            { key: 'inching', label: '碰撞检测', children: frame.boardTelemetry?.inchingSwitch ?? '-' },
            { key: 'battery', label: '电池', children: frame.boardTelemetry ? `${frame.boardTelemetry.batteryValue}% · 状态 ${frame.boardTelemetry.batteryState}` : '-' },
            { key: 'buttonEvents', label: '电源/触摸事件', children: frame.boardTelemetry ? `${frame.boardTelemetry.powerButtonEvent} / ${frame.boardTelemetry.touchButtonEvent}` : '-' },
            { key: 'codes', label: '板端告警/错误', children: frame.boardTelemetry ? `${frame.boardTelemetry.warningCode} / ${frame.boardTelemetry.errorCode}` : '-' },
            { key: 'safetyState', label: '跌落/外门/UVC', children: frame.boardTelemetry ? `${frame.boardTelemetry.fallDetectionState} / ${frame.boardTelemetry.outerDoorState} / ${frame.boardTelemetry.uvcSprayerState}` : '-' },
            { key: 'cabinetState', label: '舱门/舱锁状态', children: frame.boardTelemetry ? `${frame.boardTelemetry.cabinetDoorState} / ${frame.boardTelemetry.cabinetLockState}` : '-' },
            { key: 'doorCommands', label: '外门/UVC/舱门/舱锁指令', children: frame.boardCommand ? `${frame.boardCommand.outerDoorCommand} / ${frame.boardCommand.uvcSprayerCommand} / ${frame.boardCommand.cabinetDoorCommand} / ${frame.boardCommand.cabinetLockCommand}` : '-' },
            { key: 'commandState', label: '电机/电源/舱灯指令', children: frame.boardCommand ? `${hex(frame.boardCommand.motorState)} / ${frame.boardCommand.powerControl} / ${frame.boardCommand.cabinetLedCommand}` : '-' },
            { key: 'iq', label: '左右电机 IQ', children: frame.boardTelemetry ? `${frame.boardTelemetry.leftIq} / ${frame.boardTelemetry.rightIq}` : '-' },
            { key: 'busbar', label: '左右母线电流', children: frame.boardTelemetry ? `${frame.boardTelemetry.leftBusbarCurrent} / ${frame.boardTelemetry.rightBusbarCurrent}` : '-' },
            { key: 'dBoard', label: 'D 板状态', children: frame.boardTelemetry ? hex(frame.boardTelemetry.dBoardState) : '-' },
            { key: 'weights', label: '载重原始值', children: frame.boardTelemetry?.weights?.length ? frame.boardTelemetry.weights.join(', ') : '-' },
            { key: 'ledCommands', label: 'LED 指令', children: frame.boardCommand?.ledCommands?.length ? frame.boardCommand.ledCommands.join(', ') : '-' },
            { key: 'sonar', label: '超声掩码/指令', children: `${frame.boardTelemetry ? hex(frame.boardTelemetry.sonarMask) : '-'} / ${frame.boardCommand ? hex(frame.boardCommand.sonarCommand) : '-'}` },
            { key: 'infraredRaw', label: '板端红外原始值', span: 2, children: frame.boardTelemetry?.infraredDistance?.length ? frame.boardTelemetry.infraredDistance.join(', ') : '-' },
            { key: 'infraredGap', label: '电梯缝红外', children: frame.boardTelemetry?.infraredGap ?? '-' },
            { key: 'sonicRaw', label: '板端超声原始值', span: 3, children: frame.boardTelemetry?.ultrasonicDistance?.length ? frame.boardTelemetry.ultrasonicDistance.join(', ') : '-' },
          ]}
        />
      )}
      {frame.trajectories.length > 0 && (
        <Table rowKey="id" size="small" columns={trajectoryColumns} dataSource={frame.trajectories} pagination={{ pageSize: 8, size: 'small' }} scroll={{ x: 850 }} />
      )}
      {frame.localizationHealth && (
        <Alert
          type={frame.localizationHealth.status === 'lost' ? 'error' : frame.localizationHealth.status === 'warning' ? 'warning' : 'success'}
          showIcon
          title={`丢定位检测：${frame.localizationHealth.status}`}
          description={`风险概率 ${(frame.localizationHealth.lostProbability * 100).toFixed(3)}% · 诊断帧 ${relativeSeconds(manifest.session.startNs, frame.localizationHealth.timestampNs).toFixed(2)}s · 原始激光 ${relativeSeconds(manifest.session.startNs, frame.localizationHealth.originalLaserNs).toFixed(2)}s`}
        />
      )}
      <Alert
        type="info"
        showIcon
        title="时间选择语义"
        description="P1 数据与 P0 相同：每个主题都取 playhead 之前最后一帧；面板不会读取未来候选或未来控制量。"
      />
    </Space>
  )

  return (
    <Tabs
      className="diagnostic-tabs"
      size="small"
      items={[
        { key: 'imu', label: 'IMU', children: <IMUChart sessionID={manifest.session.id} buckets={imuBuckets} analysis={imuAnalysis} startNS={manifest.session.startNs} /> },
        { key: 'camera', label: '图像', children: cameraTab },
        { key: 'alarms', label: <span>报警事件 {frame.alarms.length ? <Tag color="red">{frame.alarms.length}</Tag> : null}</span>, children: alarmTab },
        { key: 'planner', label: 'P1 诊断', children: plannerTab },
      ]}
    />
  )
}
